<template>
  <div class="project-container">
    <header class="header">
      <div class="flex-container">
        <div class="image-container">
          <img :src="require('@/assets/image/sproogy-logo.png')" alt="Logo Sproogy" class="header-image" loading="lazy"/>
        </div>
        <div class="text-container">
          <h1>{{ $t('sproogy.title') }}</h1>
          <p class="subtitle">{{ $t('sproogy.subtitle') }}</p>
        </div>
      </div>
    </header>

    <section class="about-project">
      <h2>{{ $t('sproogy.aboutTitle') }}</h2>
      <div class="content-grid">
        <div class="text-block">
          <p v-html="$t('sproogy.about1')"></p>
          <p v-html="$t('sproogy.about2')"></p>
        </div>
        <div class="highlight-box">
           <div class="stat-item">
             <span class="mdi mdi-cube-outline stat-icon"></span>
             <span class="stat-text">Custom IoC/DI Engine</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-shield-lock-outline stat-icon"></span>
             <span class="stat-text">SSL/TLS Abstraction</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-database stat-icon"></span>
             <span class="stat-text">JPA Integration</span>
           </div>
        </div>
      </div>
    </section>

    <section class="why-sproogy">
      <h2>{{ $t('sproogy.whyTitle') }}</h2>
      <div class="why-grid">
        <div class="why-card">
          <span class="mdi mdi-leaf why-icon"></span>
          <div class="why-text" v-html="$t('sproogy.whySpring')"></div>
        </div>
        <div class="why-card">
          <span class="mdi mdi-lan-connect why-icon"></span>
          <div class="why-text" v-html="$t('sproogy.whySocket')"></div>
        </div>
      </div>
      <div class="architecture-visual">
        <div class="visual-header">
           <span class="visual-badge">Architecture Blueprint</span>
           <h3>{{ $t('sproogy.architectureTitle') }}</h3>
        </div>
        <div class="mermaid-container" id="archi-diagram">
          <!-- graph TB ... -->
        </div>
        <p class="visual-caption">Vue d'ensemble du fonctionnement interne : de la découverte des beans à l'exposition des sockets sécurisés.</p>
      </div>
    </section>

    <section class="lifecycle-details">
      <h2>{{ $t('sproogy.lifecycleTitle') }}</h2>
      <p class="centered-text" v-html="$t('sproogy.lifecycleDesc')"></p>
      <div class="lifecycle-visual">
        <div class="mermaid-container" id="lifecycle-diagram">
          <!-- sequenceDiagram ... -->
        </div>
      </div>
    </section>

    <section class="code-peek">
      <h2>{{ $t('sproogy.codeTitle') }}</h2>
      <div class="code-container">
        <div class="code-header">
          <div class="dots">
            <span class="dot red"></span>
            <span class="dot yellow"></span>
            <span class="dot green"></span>
          </div>
          <span class="file-path">Quick Peek: Hello World</span>
        </div>
        <div class="code-content">
          <pre><code><span class="t">// Main.java</span>
<span class="k">@SproogyApplication</span>(basePackage = <span class="s">"com.example"</span>)
<span class="k">public class</span> <span class="c">Main</span> {
    <span class="k">public static void</span> <span class="f">main</span>(String[] args) {
        SproogyApp.<span class="f">run</span>(Main.<span class="k">class</span>, args);
    }
}

<span class="t">// HelloController.java</span>
<span class="k">@Controller</span>
<span class="k">public class</span> <span class="c">HelloController</span> {
    <span class="k">@AppMapping</span>(<span class="s">"/hello/{name}"</span>)
    <span class="k">public</span> ResponseEntity&lt;String&gt; <span class="f">hello</span>(<span class="k">@PathVariable</span>(<span class="s">"name"</span>) String name) {
        <span class="k">return</span> ResponseEntity.<span class="f">ok</span>(<span class="s">"Hello, "</span> + name + <span class="s">"!"</span>);
    }
}

<span class="t">// SSLConfig.java</span>
<span class="k">@Component</span>
<span class="k">public class</span> <span class="c">SSLConfig</span> <span class="k">implements</span> <span class="c">SSLFactoryLoader</span> {
    <span class="k">@Override</span>
    <span class="k">public</span> SSLServerSocketFactory <span class="f">getSSLServerSocketFactory</span>() <span class="k">throws</span> Exception {
        <span class="c">KeyStore</span> keyStore = <span class="c">KeyStore</span>.<span class="f">getInstance</span>(<span class="s">"JKS"</span>);
        keyStore.<span class="f">load</span>(<span class="k">new</span> <span class="c">FileInputStream</span>(<span class="s">"keystore.jks"</span>), <span class="s">"password"</span>.<span class="f">toCharArray</span>());
        <span class="c">KeyManagerFactory</span> kmf = <span class="c">KeyManagerFactory</span>.<span class="f">getInstance</span>(<span class="s">"SunX509"</span>);
        kmf.<span class="f">init</span>(keyStore, <span class="s">"password"</span>.<span class="f">toCharArray</span>());
        <span class="c">SSLContext</span> sslContext = <span class="c">SSLContext</span>.<span class="f">getInstance</span>(<span class="s">"TLS"</span>);
        sslContext.<span class="f">init</span>(kmf.<span class="f">getKeyManagers</span>(), <span class="k">null</span>, <span class="k">null</span>);
        <span class="k">return</span> sslContext.<span class="f">getServerSocketFactory</span>();
    }
}</code></pre>
        </div>
      </div>
    </section>

    <section class="technical-details">
      <h2>{{ $t('sproogy.learningsTitle') }}</h2>
      <div class="competencies-grid">
        <InfraCompetencyCard 
          icon="mdi-code-braces"
          title="Architecture Java & IoC"
          :description="$t('sproogy.learning1')"
        />
        <InfraCompetencyCard 
          icon="mdi-shield-lock-outline"
          title="Sécurité SSL/TLS"
          :description="$t('sproogy.learning2')"
        />
        <InfraCompetencyCard 
          icon="mdi-database"
          title="Intégration JPA/Hibernate"
          :description="$t('sproogy.learning3')"
        />
        <InfraCompetencyCard 
          icon="mdi-lan-connect"
          title="Réseau & Sockets"
          :description="$t('sproogy.learning4')"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import InfraCompetencyCard from '@/component/project/infra/InfraCompetencyCard.vue';

@Component({
  components: {
    InfraCompetencyCard
  }
})
export default class SproogyComponent extends Vue {
  mounted() {
    // Wait a bit for Mermaid CDN to load
    setTimeout(() => {
      const mermaid = (window as any).mermaid;
      if (!mermaid) {
        console.warn('Mermaid not loaded yet, retrying...');
        setTimeout(() => this.renderDiagrams(), 500);
        return;
      }
      
      // Use neutral theme with high contrast that works in both light and dark modes
      mermaid.initialize({
        startOnLoad: false,
        theme: 'neutral',
        themeVariables: {
          primaryColor: '#e8e8e8',
          primaryTextColor: '#000',
          primaryBorderColor: '#333',
          lineColor: '#333',
          secondaryColor: '#d0d0d0',
          tertiaryColor: '#f5f5f5',
          mainBkg: '#ffffff',
          secondBkg: '#f0f0f0',
          tertiaryBkg: '#e8e8e8',
          textColor: '#000',
          border1: '#333',
          border2: '#666',
          arrowheadColor: '#333',
          fontFamily: 'TTInterfaces, sans-serif',
          fontSize: '14px',
          // Force black text in all nodes
          nodeBorder: '#333',
          clusterBkg: '#f5f5f5',
          clusterBorder: '#333',
          titleColor: '#000',
          edgeLabelBackground: '#ffffff',
          nodeTextColor: '#000',
          // Sequence diagram
          actorBkg: '#e8e8e8',
          actorBorder: '#333',
          actorTextColor: '#000',
          actorLineColor: '#333',
          signalColor: '#333',
          signalTextColor: '#000',
          labelBoxBkgColor: '#e8e8e8',
          labelBoxBorderColor: '#333',
          labelTextColor: '#000',
          loopTextColor: '#000',
          noteBorderColor: '#333',
          noteBkgColor: '#fff5e1',
          noteTextColor: '#000',
          activationBorderColor: '#333',
          activationBkgColor: '#d0d0d0',
          sequenceNumberColor: '#000'
        }
      });
    this.renderDiagrams();
  }, 100);
  }

  async renderDiagrams() {
    try {
      const mermaid = (window as any).mermaid;
      if (!mermaid) {
        console.error('Mermaid not loaded from CDN');
        return;
      }
    
    const archiDiagram = `
graph TB
    subgraph "Your Application"
        Main["@SproogyApplication<br/>Main Class"]
        Controller["@Controller<br/>Request Handlers"]
        Service["@Service<br/>Business Logic"]
        Repository["@Repository<br/>Data Access"]
    end

    subgraph "Sproogy Framework"
        IoC[IoC Container<br/>Dependency Injection]
        SocketServer[SSL Socket Server<br/>Multi-threaded]
        FilterChain[Filter Chain<br/>Request/Response Processing]
        JPA[JPA Module<br/>Hibernate Integration]
    end

    Client[SSL Socket Client]
    Database[(Database)]

    Client -->|SSL Connection| SocketServer
    SocketServer --> FilterChain
    FilterChain --> Controller
    Controller --> Service
    Service --> Repository
    Repository --> JPA
    JPA --> Database

    IoC -.Manages.-> Controller
    IoC -.Manages.-> Service
    IoC -.Manages.-> Repository

    Main -->|Bootstraps| IoC
    IoC -->|Initializes| SocketServer
    `;

    const lifecycleDiagram = `
sequenceDiagram
    participant Main as main()
    participant Sproogy as SproogyApp
    participant Env as EnvLoader
    participant Scanner as ComponentScanner
    participant Loader as SproogyLoader
    participant DI as Dependency Injection
    participant Bootstrap as SproogyBootStrap
    participant Server as Server
    participant Hook as Shutdown Hook

    Main->>Sproogy: run(Main.class, args)

    rect rgb(200, 220, 240)
    Note over Sproogy,Env: Phase 1: Validation & Configuration
    Sproogy->>Sproogy: Validate @SproogyApplication
    Sproogy->>Env: Load .env file
    Env-->>Sproogy: Environment ready
    end

    rect rgb(220, 240, 200)
    Note over Scanner,Loader: Phase 2: Component Discovery
    Sproogy->>Scanner: Scan basePackage + com.sproogy
    Scanner-->>Sproogy: List of components
    Sproogy->>Loader: Run pre-DI loaders
    Note right of Loader: JPA: Scan entities, configure Hibernate
    Loader-->>Sproogy: Modules prepared
    end

    rect rgb(240, 220, 200)
    Note over DI: Phase 3: Dependency Injection
    Sproogy->>DI: Build dependency graph
    DI->>DI: Detect circular dependencies
    DI->>DI: Topological sort
    DI->>DI: Instantiate beans
    DI->>DI: Inject @Autowired fields
    DI->>DI: Inject @Value/@Env fields
    DI-->>Sproogy: ApplicationContext ready
    end

    rect rgb(240, 200, 220)
    Note over Bootstrap,Server: Phase 4: Post-DI Initialization
    Sproogy->>Bootstrap: Run post-DI bootstraps
    Note right of Bootstrap: Server: Initialize routes, filters
    Bootstrap-->>Sproogy: Initialization complete
    Sproogy->>Server: Start server (if ServerFactory bean exists)
    Server->>Hook: Register shutdown hook
    Server-->>Sproogy: Server running
    end

    Sproogy-->>Main: Application running

    Note over Main,Server: Application runs normally...

    rect rgb(220, 220, 220)
    Note over Hook,Server: Phase 5: Shutdown
    Hook->>Server: JVM shutdown detected
    Server->>Server: Stop accepting connections
    Server->>Server: Wait for active requests
    Server->>Server: Close sockets
    Server-->>Hook: Shutdown complete
    end
    `;

    const {svg: archiSvg} = await mermaid.render('archi-svg', archiDiagram);
    const archiEl = document.getElementById('archi-diagram');
    if (archiEl) archiEl.innerHTML = archiSvg;

    const {svg: lifecycleSvg} = await mermaid.render('lifecycle-svg', lifecycleDiagram);
    const lifecycleEl = document.getElementById('lifecycle-diagram');
    if (lifecycleEl) lifecycleEl.innerHTML = lifecycleSvg;
    } catch (error) {
      // Suppress Mermaid rendering errors (happens when scrolling fast)
      console.debug('Mermaid rendering skipped:', error);
    }
  }
}
</script>

<style scoped>
.project-container {
  font-family: 'TTInterfaces', sans-serif;
  line-height: 1.7;
  color: var(--ln-text-primary);
  background-color: transparent;
  width: 100%;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
  padding: 2rem 1.5rem;
  box-sizing: border-box;
  overflow-x: hidden;
}

.header {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: var(--ln-glass-elevated);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-lg);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 12px 48px var(--ln-glass-shadow);
}

.flex-container {
  display: flex;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
  justify-content: center;
}

.image-container {
  width: 220px;
  flex-shrink: 0;
}

.text-container {
  flex: 1 1 400px;
  text-align: left;
}

.header h1 {
  font-size: clamp(2rem, 8vw, 3.5rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -1px;
}

.subtitle {
  font-size: 1.4rem;
  color: var(--ln-text-secondary);
  font-weight: 500;
  opacity: 0.9;
}

.header-image {
  width: 100%;
  max-width: 220px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  transition: transform 0.5s ease;
}

.header-image:hover {
  transform: scale(1.05) rotate(2deg);
}

section {
  margin-bottom: 2rem;
  padding: 2rem;
  background: var(--ln-glass-base);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 8px 32px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
  width: 100%;
  box-sizing: border-box;
}

section:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px var(--ln-glass-shadow-elevated);
}

h2 {
  font-size: clamp(1.5rem, 5vw, 2.2rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 2rem;
  position: relative;
  padding-bottom: 1rem;
}

h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 80px;
  height: 4px;
  background: linear-gradient(to right, var(--ln-teal-deep), var(--ln-teal-light));
  border-radius: 2px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 3rem;
  align-items: center;
}

.highlight-box {
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border-strong);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item:last-child { margin-bottom: 0; }

.stat-icon { font-size: 2.2rem; color: var(--ln-teal-deep); }
.stat-text { font-weight: 600; color: var(--ln-text-primary); }

.why-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
}

.why-card {
  display: flex;
  gap: 1.5rem;
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
}

.why-icon { font-size: 3rem; color: var(--ln-teal-deep); }

.why-text {
  font-size: 1.05rem;
  color: var(--ln-text-secondary);
}

.architecture-visual,
.lifecycle-visual {
  margin-top: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-surface);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  text-align: center;
}

.visual-header { margin-bottom: 2rem; }
.visual-badge {
  background: var(--ln-teal-deep);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.doc-image {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
}

.visual-caption {
  margin-top: 1.5rem;
  font-size: 0.95rem;
  font-style: italic;
  opacity: 0.8;
}

.code-container {
  background: #1e2227;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0,0,0,0.4);
}

[data-theme="light"] .code-container {
  background: #f5f5f5;
  box-shadow: 0 20px 50px rgba(0,0,0,0.15);
}

.code-header {
  background: #282c34;
  padding: 0.8rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #3e4451;
}

[data-theme="light"] .code-header {
  background: #e0e0e0;
  border-bottom: 1px solid #ccc;
}

.dots { display: flex; gap: 8px; }
.dot { width: 12px; height: 12px; border-radius: 50%; }
.red { background: #ff5f56; }
.yellow { background: #ffbd2e; }
.green { background: #27c93f; }

.file-path { 
  color: #abb2bf; 
  font-size: 0.85rem; 
  font-family: monospace; 
}

[data-theme="light"] .file-path {
  color: #666;
}

.code-content { 
  padding: 1.5rem; 
  overflow-x: auto; 
}

pre { 
  margin: 0; 
  font-family: 'JetBrains Mono', 'Courier New', monospace; 
  font-size: 0.95rem;
  color: #abb2bf;
}

[data-theme="light"] pre {
  color: #24292e;
}

/* Dark mode syntax highlighting */
.k { color: #c678dd; } /* keyword */
.c { color: #e5c07b; } /* class */
.f { color: #61afef; } /* function */
.s { color: #98c379; } /* string */
.t { color: #5c6370; font-style: italic; } /* comment */

/* Light mode syntax highlighting */
[data-theme="light"] .k { color: #d73a49; } /* keyword - red */
[data-theme="light"] .c { color: #6f42c1; } /* class - purple */
[data-theme="light"] .f { color: #005cc5; } /* function - blue */
[data-theme="light"] .s { color: #22863a; } /* string - green */
[data-theme="light"] .t { color: #6a737d; font-style: italic; } /* comment - gray */

.mermaid-container {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  padding: 1rem;
  display: flex;
  justify-content: flex-start; /* Better for scrolling on mobile */
  background: white;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
  box-sizing: border-box;
}

@media (min-width: 769px) {
  .mermaid-container {
    justify-content: center;
  }
}

/* Dark mode: add strong border to make white diagrams stand out */
[data-theme="dark"] .mermaid-container {
  border: 3px solid #2c7a7b;
  box-shadow: 0 0 20px rgba(44, 122, 123, 0.3);
}

/* Force black text in Mermaid diagrams ALWAYS (override theme styles) */
/* Only target text elements, NOT shapes */
.mermaid-container :deep(svg text),
.mermaid-container :deep(svg tspan) {
  fill: #000 !important;
}

/* Force black for HTML elements inside Mermaid foreignObject (like <p> tags) */
.mermaid-container :deep(foreignObject p),
.mermaid-container :deep(foreignObject span),
.mermaid-container :deep(foreignObject div),
.mermaid-container :deep(.nodeLabel p),
.mermaid-container :deep(.nodeLabel span) {
  color: #000 !important;
}

.mermaid-container svg .actor-line {
  stroke: #666 !important;
}

.mermaid-container svg rect.actor {
  fill: #e8e8e8 !important;
  stroke: #666 !important;
}

.competencies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.centered-text { text-align: center; max-width: 700px; margin: 0 auto 2rem; }

@media (max-width: 992px) {
  .content-grid { grid-template-columns: 1fr; }
  .why-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .header { padding: 1.5rem; margin-bottom: 1.5rem; }
  .flex-container { gap: 1rem; flex-direction: column; }
  .image-container { width: 150px; flex-shrink: 0; }
  .header-image { max-width: 150px; }
  .text-container { text-align: center; flex: 1 1 auto; }
  section { padding: 1.5rem; margin-bottom: 1.5rem; }
  h2 { font-size: 1.5rem; margin-bottom: 1.5rem; }
  
  .architecture-visual,
  .lifecycle-visual {
    padding: 1.2rem;
  }

  /* Mermaid diagrams mobile optimization */
  .mermaid-container {
    padding: 0.5rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .mermaid-container svg {
    min-width: 600px; /* Force minimum width to keep it readable, but it triggers horizontal scroll inside container */
    height: auto;
  }

  .competencies-grid {
    grid-template-columns: 1fr;
  }
}
</style>
